import React, { FC, useState, useEffect } from 'react';
import { Menu, Modal, Icon, Button } from 'semantic-ui-react';
import { useHistory } from 'react-router-dom';
import { StyledContainer, Flexbox } from '@src/views/containers';
import { FlexAlign, FlexJustify, Size, CorvalUIColor, SemanticUIColor } from '@src/types';
import { BasicDialog } from '@src/views/components/modals/BasicDialog';
import { BasicDialogContainer } from '@src/views/components/modals/BasicDialogContainer';
import { RouterUtility } from '@src/util';
import AIChatBox from './AIChatBox';
import { useAiContext } from './AiContext';
import './Dash.scss';
import AIObjectPicker, { SelectedItem } from './AIObjectPicker';

export type ActiveTab = 'activity' | 'project' | 'dashboard' | 'assistant';

export const TAB_OBJECT_TYPES: Record<string, string[]> = {
  activity:  ['Activity_Id', 'Static_Project_Activity_Id'],
  project:   ['Project_Id', 'Project_Version_Id'],
  dashboard: ['Category_Id', 'Sub_Category_Id', 'Sub_Objective_Id'],
  assistant: ['Activity_Id', 'Static_Project_Activity_Id', 'Project_Id', 'Project_Version_Id', 'Category_Id', 'Sub_Category_Id', 'Sub_Objective_Id'],
};

export interface AIContextPillsProps {
  selectedObjects: SelectedItem[];
  highlightedTypes: Set<string>;
  onRemove: (objectId: string) => void;
}

export const AIContextPills: FC<AIContextPillsProps> = ({ selectedObjects, highlightedTypes, onRemove }) => {
  if (selectedObjects.length === 0) return null;

  return (
    <>
      <StyledContainer
        bgColor={CorvalUIColor.WHITE}
        padding={[Size.SM, Size.MD, Size.SM, Size.MD]}
        style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}
      >
        {selectedObjects.map((item) => {
          const isHighlighted = highlightedTypes.has(item.objectType);
          return (
            <StyledContainer
              key={item.objectId}
              as="span"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: '24px',
                padding: '6px 14px',
                fontSize: '13px',
                border: '1px solid',
                borderColor: isHighlighted ? '#a682db' : '#e0e0e0',
                backgroundColor: isHighlighted ? '#F3EAFC' : '#F9FAFB',
                color: isHighlighted ? '#532692' : '#666',
                cursor: 'default',
                transition: 'all 0.2s ease',
              }}
            >
              <span style={{ fontWeight: 700, marginRight: 8 }}>{item.name}</span>
              <Icon
                name="close"
                size="small"
                style={{ cursor: 'pointer', margin: 0, opacity: 0.7, color: isHighlighted ? '#532692' : '#666' }}
                onClick={() => onRemove(item.objectId)}
              />
            </StyledContainer>
          );
        })}
      </StyledContainer>
    </>
  );
};

export interface AIChatContainerProps {
  activeTab: ActiveTab;
  selectedObjects: SelectedItem[];
  disableTabFiltering?: boolean;
}

export const AIChatContainer: FC<AIChatContainerProps> = ({
  activeTab,
  selectedObjects,
  disableTabFiltering = false,
}) => {
  const allowedLookupObjectTypes = disableTabFiltering
    ? undefined
    : TAB_OBJECT_TYPES[activeTab];

  const sharedChatProps = {
    allowedLookupObjectTypes,
  };

  return (
    <StyledContainer style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {activeTab === 'activity'  && <AIChatBox agentType="activity"  {...sharedChatProps} />}
      {activeTab === 'project'   && <AIChatBox agentType="project"   {...sharedChatProps} />}
      {activeTab === 'dashboard' && <AIChatBox agentType="dashboard" {...sharedChatProps} />}
      {activeTab === 'assistant' && <AIChatBox agentType="assistant" {...sharedChatProps} />}
    </StyledContainer>
  );
};

interface AIPanelBodyProps {
  showMaximize?: boolean;
}

export const AIPanelBody: FC<AIPanelBodyProps> = ({ showMaximize = false }) => {
  const { activeAgentType, selectedObjects, setSelectedObjects, setShowAi } = useAiContext();
  const [activeTab, setActiveTab] = useState<ActiveTab>('activity');
  const [pickerOpen, setPickerOpen] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (activeAgentType in TAB_OBJECT_TYPES) {
      setActiveTab(activeAgentType as ActiveTab);
    }
  }, [activeAgentType]);

  const highlightedTypes = new Set(TAB_OBJECT_TYPES[activeTab] || []);

  const removeSelectedObject = (objectId: string) =>
    setSelectedObjects(prev => prev.filter(o => o.objectId !== objectId));

  const handlePickerClose = (selected: SelectedItem[]) => {
    setSelectedObjects(selected);
    setPickerOpen(false);
  };

  const handleMaximize = () => {
    setShowAi(false);
    history.push(RouterUtility.getRoutePath('DORA_AI') as string);
  };

  return (
    <>
      <StyledContainer
        className="dash-ai-panel-static dash-ai-panel"
        bgColor={CorvalUIColor.WHITE}
        style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%', minHeight: 0 }}
      >
        <StyledContainer className="ai-topbar" style={{ flex: '0 0 auto' }}>
          <StyledContainer
            bgColor={CorvalUIColor.WHITE}
            borderSize={[null, null, Size.SM, null]}
            borderColor={CorvalUIColor.DK_SCREEN_25}
            padding={[Size.SM, Size.MD, null, Size.MD]}
          >
            <Flexbox justify={FlexJustify.SPACE_BETWEEN} align={FlexAlign.CENTER}>
              <Menu secondary pointing style={{ marginBottom: 0, borderBottom: 'none', paddingLeft: '0' }}>
                {(['activity', 'project', 'dashboard', 'assistant'] as ActiveTab[]).map(tab => (
                  <Menu.Item
                    key={tab}
                    name={tab.charAt(0).toUpperCase() + tab.slice(1)}
                    active={activeTab === tab}
                    onClick={() => setActiveTab(tab)}
                  />
                ))}
              </Menu>
              <Flexbox align={FlexAlign.CENTER} style={{ gap: '4px' }}>
                {showMaximize && (
                  <Button
                    icon="expand"
                    size="mini"
                    basic
                    onClick={handleMaximize}
                    title="Open AI Assistant page"
                    style={{ boxShadow: 'none' }}
                  />
                )}
                <Button
                  icon="add"
                  size="mini"
                  basic
                  className="ai-add-btn"
                  onClick={() => setPickerOpen(true)}
                  title="Select Objects for Analysis"
                  style={{ boxShadow: 'none' }}
                />
              </Flexbox>
            </Flexbox>
          </StyledContainer>

          <AIContextPills
            selectedObjects={selectedObjects}
            highlightedTypes={highlightedTypes}
            onRemove={removeSelectedObject}
          />
          {selectedObjects.length > 0 && (
            <StyledContainer
                borderSize={[null, null, Size.SM, null]}
                borderColor={CorvalUIColor.DK_SCREEN_25}
            />
          )}
        </StyledContainer>

        <StyledContainer
          className="ai-scroll-body"
          style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        >
          <AIChatContainer activeTab={activeTab} selectedObjects={selectedObjects} />
        </StyledContainer>
      </StyledContainer>

      <AIObjectPicker open={pickerOpen} onClose={handlePickerClose} />
    </>
  );
};

const DashAIPanel: FC<{ open?: boolean; onClose?: () => void }> = ({ open, onClose }) => {
  const { setShowAi } = useAiContext();

  const panel = (
    <BasicDialog
      hasSystemContext={false}
      showCancelButton={false}
      showSubmitButton={false}
      className="no-padding"
    >
      <BasicDialogContainer
        hasSystemContext={false}
        title="AI Assistant"
        titleColor={SemanticUIColor.PURPLE}
        onClose={onClose ?? (() => setShowAi(false))}
        contentHeight="75vh"
        contentMaxHeight="90vh"
        contentPadding={null}
        overflow={false}
        containerStyles={{ height: '100%' }}
      >
        <AIPanelBody showMaximize />
      </BasicDialogContainer>
    </BasicDialog>
  );

  if (open) {
    return (
      <Modal open={open} onClose={onClose ?? (() => setShowAi(false))} className="dash-ai-panel-modal" size="large">
        {panel}
      </Modal>
    );
  }

  return panel;
};

export default DashAIPanel;
